'use client';

import { useId } from 'react';
import Link from 'next/link';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { Box } from '@mui/material';
import { Button, Stack } from '@/primitives';
import { slate } from '@/lib/colours';
import { ErrorCowCard } from '@/components/utils/ErrorCowCard';
import { PublicErrorSection } from '@/components/publicSearch/PublicErrorSection';

type PublicEntityNotFoundCardProps = {
  /** Singular entity name, e.g. "animal" or "member". Used in the title and body copy. */
  entityLabel: string;
  /** The ID from the URL that couldn't be resolved. */
  entityId: string;
  /** Where the primary CTA links — typically the entity list page. */
  listHref: string;
  /** Label for the primary CTA button, e.g. "Back to animal search". */
  listLabel: string;
  /** When provided, renders a secondary "Go back" button that calls this handler. */
  onGoBack?: () => void;
};

export function PublicEntityNotFoundCard({
  entityLabel,
  entityId,
  listHref,
  listLabel,
  onGoBack,
}: PublicEntityNotFoundCardProps) {
  const titleId = useId();

  return (
    <PublicErrorSection ariaLabelledBy={titleId}>
      <ErrorCowCard
        titleId={titleId}
        label={`404 · ${entityLabel} not found`}
        title={`We couldn't find that ${entityLabel}`}
        description={
          <>
            {`No ${entityLabel} matches the ID `}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 1,
                py: 0.25,
                mx: 0.25,
                borderRadius: '6px',
                bgcolor: slate[100],
                color: slate[800],
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.9em',
                fontWeight: 600,
                border: `1px solid ${slate[200]}`,
              }}
            >
              {entityId}
            </Box>
            {` in this organisation. Check the URL for typos, or head back to the ${entityLabel} list.`}
          </>
        }
        actions={
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              justifyContent: 'center',
            }}
          >
            {onGoBack && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={onGoBack}
                sx={{ textTransform: 'none', px: 2.5, py: 1 }}
              >
                Go back
              </Button>
            )}
            <Button
              component={Link}
              href={listHref}
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ textTransform: 'none', px: 2.5, py: 1 }}
            >
              {listLabel}
            </Button>
          </Stack>
        }
      />
    </PublicErrorSection>
  );
}
